import React, { useEffect, useCallback, useRef, useState } from "react";
import _ from "lodash";
// @material-ui/core components
import {
  Box,
  Checkbox,
  Fab,
  Grid,
  IconButton,
  InputAdornment,
  makeStyles,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TablePagination,
  TableRow,
  Tooltip,
  CircularProgress,
  Backdrop,
  Button,
} from "@material-ui/core";
import { useSelector } from "react-redux";
// core components
import GridItem from "../../components/Grid/GridItem.js";
import GridContainer from "../../components/Grid/GridContainer.js";
// import Table from "../../components/Table/Table.js";
import Card from "../../components/Card/Card.js";
import CardHeader from "../../components/Card/CardHeader.js";
import CardBody from "../../components/Card/CardBody.js";
import AddIcon from "@material-ui/icons/Add";
import DiscountAdd from "./DiscountAdd";
import { CloudDownload, Search } from "@material-ui/icons";
import VisibilityIcon from "@material-ui/icons/Visibility";
import EditIcon from "@material-ui/icons/Edit";
import DeleteIcon from "@material-ui/icons/Delete";
import DiscountView from "./DiscountView";
import DiscountEdit from "./DiscountEdit";
import CustomInput from "../../components/CustomInput/CustomInput";
import People from "@material-ui/icons/People";
import axios from "axios";
import { GetAllAccountsAPI, UpdateAccountAPI } from "../../config/api";
import { ConfirmationDialog } from "../../components/common";
import { toast } from "react-toastify";
import { ImportExcel } from "../Master/ImportExcel";
import { CloudUpload } from "@material-ui/icons";
import * as XLSX from 'xlsx';

const useStyles = makeStyles((theme) => ({
  fab: {
    position: "absolute",
    bottom: theme.spacing(2),
    right: theme.spacing(2),
  },
  cardCategoryWhite: {
    "&,& a,& a:hover,& a:focus": {
      color: "rgba(255,255,255,.62)",
      margin: "0",
      fontSize: "14px",
      marginTop: "0",
      marginBottom: "0",
    },
    "& a,& a:hover,& a:focus": {
      color: "#FFFFFF",
    },
  },
  cardTitleWhite: {
    color: "#FFFFFF",
    marginTop: "0px",
    minHeight: "auto",
    fontWeight: "300",
    fontFamily: "'Roboto', 'Helvetica', 'Arial', sans-serif",
    marginBottom: "3px",
    textDecoration: "none",
    "& small": {
      color: "#777",
      fontSize: "65%",
      fontWeight: "400",
      lineHeight: "1",
    },
  },
}));

const columns = [
  { id: "accountCode", label: "Party Code" },
  { id: "name", label: "Party Name" },
  { id: "discountedProducts", label: "Discounted Products" },
  // { id: "createdByName", label: "Discount Provided By" },
  { id: "action", label: "Action" },
];

export default function DiscountList() {
  const classes = useStyles();
  let authToken = useSelector((state) => state.userReducer.authToken);
  let userInfo = useSelector((state) => state.userReducer.userInfo);

  const [accounts, setAccounts] = useState([]);
  const [openAddDialog, setOpenAddDialog] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [openViewDialog, setOpenViewDialog] = useState(null);
  const [openEditDialog, setOpenEditDialog] = useState(null);
  const [openImportDialog, setOpenImportDialog] = useState(false);
  const [tota, setTota] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [selectedRows, setSelectedRows] = useState([]);
  const [openClearAllConfirm, setOpenClearAllConfirm] = useState(null);
  const hasInitialLoad = useRef(false);
  const isLoadingRef = useRef(false); // Ref to prevent duplicate API calls

  // Memoize _getDiscounts to prevent unnecessary re-renders
  const _getDiscounts = useCallback(async (pageNum, rowsPerPageNum, searchValue = "") => {
    // Prevent duplicate calls
    if (isLoadingRef.current) {
      return;
    }

    try {
      isLoadingRef.current = true;
      setLoading(true);
      const skipm = pageNum * rowsPerPageNum;
      let query = `&limit=${rowsPerPageNum}&skip=${skipm}`;

      if (searchValue) {
        query += `&search=${encodeURIComponent(searchValue)}`;
      }

      let res = await axios.get(
        GetAllAccountsAPI(userInfo.entity) + query,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setAccounts(res.data.accounts || []);
      setTota(res.data.total || 0);
    } catch (err) {
      console.log("Error:  ", err);
      toast.error("Something went wrong, Please Contact Admin", {
        position: "top-center",
        autoClose: 1500,
      });
      setAccounts([]);
      setTota(0);
    } finally {
      setLoading(false);
      isLoadingRef.current = false;
    }
  }, [userInfo.entity, authToken]);

  // Initial load: only on mount
  useEffect(() => {
    let isMounted = true;
    const loadData = async () => {
      await _getDiscounts(0, rowsPerPage, "");
      if (isMounted) {
        hasInitialLoad.current = true; // Mark initial load complete AFTER API call
      }
    };
    loadData();
    return () => {
      isMounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Effect for pagination changes (when page or rowsPerPage changes)
  useEffect(() => {
    // Skip until initial load is complete
    if (!hasInitialLoad.current) {
      return;
    }
    _getDiscounts(page, rowsPerPage, searchTerm);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]); // Only pagination changes trigger this

  // Refresh when edit dialog closes (only after initial load)
  const prevEditDialogRef = useRef(null);
  useEffect(() => {
    if (!hasInitialLoad.current) {
      prevEditDialogRef.current = openEditDialog;
      return;
    }
    // Only refresh when dialog transitions from open to closed
    const wasOpen = prevEditDialogRef.current && prevEditDialogRef.current !== null;
    const isNowClosed = !openEditDialog || openEditDialog === null;

    if (wasOpen && isNowClosed) {
      // Dialog was open and is now closed - refresh data
      _getDiscounts(page, rowsPerPage, searchTerm);
    }

    prevEditDialogRef.current = openEditDialog;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openEditDialog]);

  // async function _getDiscounts() {
  //   try {
  //     let res = await axios.get(GetAllAccountsAPI(userInfo.entity), {
  //       headers: {
  //         Authorization: `Bearer ${authToken}`,
  //       },
  //     });
  //     setAccounts(res.data.accounts);
  //   } catch (err) {
  //     // console.log(err);
  //     // toast.error(("Something went wrong, Please Contact Admin") , {
  //     //   position: "top-center",
  //     //   autoClose: 1500,
  //     //   hideProgressBar: false,
  //     //   closeOnClick: true,
  //     //   pauseOnHover: true,
  //     //   draggable: true,
  //     //   progress: undefined,
  //     //   theme: "light",
  //     //   }); 
  //   }
  // }

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPage(0);
  };

  const handleClearAllDiscountsSelected = async () => {
    if (!selectedRows.length) return;
    try {
      for (const accountId of selectedRows) {
        await axios.patch(
          UpdateAccountAPI(accountId),
          { discounts: [] },
          { headers: { Authorization: `Bearer ${authToken}` } }
        );
      }
      toast.success(
        `Cleared all discounts for ${selectedRows.length} selected account(s).`,
        { position: "top-center", autoClose: 3000 }
      );
      setSelectedRows([]);
      refreshDiscounts();
    } catch (err) {
      toast.error(err.response?.data?.message || "Failed to clear discounts", {
        position: "top-center",
        autoClose: 3000,
      });
    }
    setOpenClearAllConfirm(null);
  };

  function closeAddDialog() {
    setOpenAddDialog(false);
  }

  function closeViewDialog() {
    setOpenViewDialog(null);
  }

  function closeEditDialog() {
    setOpenEditDialog(null);
  }

  // Debounced search effect
  useEffect(() => {
    if (!hasInitialLoad.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      if (searchTerm) {
        setPage(0);
        _getDiscounts(0, rowsPerPage, searchTerm);
      } else {
        setPage(0);
        _getDiscounts(0, rowsPerPage, "");
      }
    }, 300);

    return () => clearTimeout(timeoutId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchTerm]);

  // Function to refresh data (called explicitly after successful operations)
  const refreshDiscounts = useCallback(() => {
    _getDiscounts(page, rowsPerPage, searchTerm);
  }, [page, rowsPerPage, searchTerm, _getDiscounts]);

  let data = accounts;

  // if (searchTerm !== "") {
  //   data = _.filter(data, (i) => {
  //     return i.name && i.name.toLowerCase().includes(searchTerm.toLowerCase());
  //   });
  // }

  function handleSearch() {
    setSearchTerm(search);
    setPage(0); // Reset to first page on new search
  }

  // Export function using XLSX
  const handleExport = async () => {
    setIsExporting(true);
    const loadingToastId = toast.loading('🔄 Preparing export... Please wait', {
      position: "top-center",
      autoClose: false,
      hideProgressBar: false,
      closeOnClick: false,
      pauseOnHover: true,
      draggable: false,
      progress: undefined,
      theme: "colored",
    });

    try {
      // First, get the total count
      toast.update(loadingToastId, {
        render: '📊 Fetching total record count...',
        type: 'info',
        isLoading: true,
      });

      const totalRes = await axios.get(
        GetAllAccountsAPI(userInfo.entity) + `&limit=1&skip=0`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      const total = totalRes.data.total || 0;

      if (total === 0) {
        toast.update(loadingToastId, {
          render: '❌ No data to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      // Fetch all data using the total count
      toast.update(loadingToastId, {
        render: `📥 Downloading ${total} records... This may take a moment`,
        type: 'info',
        isLoading: true,
      });

      const res = await axios.get(
        GetAllAccountsAPI(userInfo.entity) + `&limit=${total}&skip=0`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      const allAccounts = res.data.accounts || [];

      // Flatten discounts data
      toast.update(loadingToastId, {
        render: '⚙️ Processing data for export...',
        type: 'info',
        isLoading: true,
      });

      let exportData = [];
      allAccounts.forEach((account) => {
        if (account.discounts && account.discounts.length > 0) {
          account.discounts.forEach((discount) => {
            const { product, discountPercent, discountPrice } = discount;
            exportData.push({
              "Account Name": account.accountName || account.name || "",
              "Account Code": account.accountCode || "",
              "Product Name": product?.productName || "",
              "Product Code": product?.productCode || "",
              "Lab": product?.lab || "",
              "Brand": product?.brand || "",
              "Price": product?.price || "",
              "Index": product?.index || "",
              "Dia": product?.dia || "",
              "Fitting Height": product?.fittingHeight || "",
              "Power Range": product?.powerRange || "",
              "Coating": product?.coating || "",
              "Quantity": product?.quantity || "",
              "Discount Percent": discountPercent || "",
              "Discount Price": discountPrice || "",
            });
          });
        }
      });

      if (exportData.length === 0) {
        toast.update(loadingToastId, {
          render: '❌ No discount data found to export!',
          type: 'error',
          isLoading: false,
          autoClose: 3000,
        });
        setIsExporting(false);
        return;
      }

      toast.update(loadingToastId, {
        render: '💾 Generating Excel file...',
        type: 'info',
        isLoading: true,
      });

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Discounts');
      XLSX.writeFile(workbook, 'discounts_export.xlsx');

      toast.update(loadingToastId, {
        render: `✅ Successfully exported ${exportData.length} records to Excel!`,
        type: 'success',
        isLoading: false,
        autoClose: 4000,
        style: {
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } catch (error) {
      console.error('Export error:', error);
      toast.update(loadingToastId, {
        render: `❌ Export failed: ${error.response?.data?.message || error.message || 'Unknown error'}`,
        type: 'error',
        isLoading: false,
        autoClose: 5000,
        style: {
          background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
          color: '#fff',
          fontWeight: 'bold',
          fontSize: '14px',
        },
      });
    } finally {
      setIsExporting(false);
    }
  };

  // Download import template (same headers as export, no data)
  const handleDownloadTemplate = () => {
    const templateHeadings = [
      "Account Name",
      "Account Code",
      "Product Name",
      "Product Code",
      "Lab",
      "Brand",
      "Price",
      "Index",
      "Dia",
      "Fitting Height",
      "Power Range",
      "Coating",
      "Quantity",
      "Discount Percent",
      "Discount Price",
    ];
    const templateData = [templateHeadings];
    const worksheet = XLSX.utils.aoa_to_sheet(templateData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Discounts");
    XLSX.writeFile(workbook, "discounts_import_template.xlsx");
    toast.success("Import template downloaded!", {
      position: "top-center",
      autoClose: 2000,
    });
  };

  return (
    <GridContainer>
      <GridItem xs={12} sm={12} md={12}>
        <Card>
          <CardHeader color="primary">
            <div
              style={{
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
                alignItems: "center",
              }}
            >
              <h4 className={classes.cardTitleWhite}>Discounts</h4>
              <div
                style={{
                  display: "flex",
                  flexDirection: "row",
                  justifyContent: "space-evenly",
                  alignItems: "center",
                }}
              >
                <CustomInput
                  id="search"
                  formControlProps={{
                    fullWidth: true,
                  }}
                  inputProps={{
                    style: {
                      marginTop: 0,
                      marginRight: 10,
                      color: "white",
                    },
                    placeholder: "Search",
                    value: search,
                    onChange: (e) => setSearch(e.target.value),
                    endAdornment: (
                      <InputAdornment position="end">
                        <People />
                      </InputAdornment>
                    ),
                  }}
                />
                <IconButton color={"secondary"} onClick={(e) => {
                  // setSearchTerm(e.target.value),
                  // handleKeyPress(e)
                  // handleKeyPress()
                  handleSearch()


                }}>
                  <Search />
                </IconButton>
                {selectedRows.length > 0 ? (
                  <Button
                    size="small"
                    variant="outlined"
                    style={{ color: "white", borderColor: "rgba(255,255,255,0.7)", marginRight: 8 }}
                    onClick={() => setOpenClearAllConfirm(true)}
                  >
                    Clear all discounts ({selectedRows.length} selected)
                  </Button>
                ) : null}
                <IconButton
                  aria-label="add"
                  style={{ color: "white" }}
                  onClick={() => setOpenAddDialog(true)}
                >
                  <AddIcon />
                </IconButton>
              </div>
            </div>
          </CardHeader>
          <CardBody>
            <Table stickyHeader aria-label="sticky table">
              <TableHead>
                <TableRow>
                  <TableCell padding="checkbox">
                    <Checkbox
                      style={{ color: "#fff" }}
                      onChange={(e) => {
                        if (e.target.checked) {
                          setSelectedRows(data.map((r) => r._id));
                        } else {
                          setSelectedRows([]);
                        }
                      }}
                      checked={data.length > 0 && selectedRows.length === data.length}
                      inputProps={{ "aria-label": "select all" }}
                    />
                  </TableCell>
                  {columns.map((column) => (
                    <TableCell
                      key={column.id}
                      align={"center"}
                      style={{ minWidth: column.minWidth }}
                    >
                      <Box
                        display={"flex"}
                        justifyContent={"center"}
                        alignItems={"center"}
                      >
                        {column.label}
                        {column.id === "action" ? (
                          <Box justifyContent={"center"} alignItems={"center"}>
                            <Tooltip title={"Export to excel"}>
                              <IconButton
                                color={"primary"}
                                onClick={handleExport}
                                disabled={isExporting}
                              >
                                <CloudDownload />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={"Download import template"}>
                              <IconButton
                                color={"primary"}
                                onClick={handleDownloadTemplate}
                              >
                                <CloudDownload />
                              </IconButton>
                            </Tooltip>
                            <Tooltip title={"Import Excel to database"}>
                              <IconButton
                                color={"primary"}
                                onClick={() => setOpenImportDialog(true)}
                              >
                                <CloudUpload />
                              </IconButton>
                            </Tooltip>
                          </Box>
                        ) : null}
                      </Box>
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {data
                  .map((row) => {
                    return (
                      <TableRow
                        hover
                        role="checkbox"
                        tabIndex={-1}
                        key={row._id || row.code}
                      >
                        <TableCell padding="checkbox">
                          <Checkbox
                            style={{ color: "#512da8" }}
                            onChange={() => {
                              const copy = selectedRows.includes(row._id)
                                ? selectedRows.filter((id) => id !== row._id)
                                : [...selectedRows, row._id];
                              setSelectedRows(copy);
                            }}
                            checked={selectedRows.includes(row._id)}
                            inputProps={{ "aria-label": `select ${row.name || row.code}` }}
                          />
                        </TableCell>
                        {columns.map((column) => {
                          let value = row[column.id];
                          if (column.id === "discountedProducts") {
                            value = !_.isEmpty(row.discounts)
                              ? row.discounts.length
                              : 0;
                          }
                          if (column.id === "action") {
                            return (
                              <TableCell align={"center"}>
                                <Box display={"flex"} justifyContent={"center"}>
                                  <IconButton
                                    aria-label="view"
                                    onClick={() =>
                                      setOpenViewDialog({
                                        open: true,
                                        item: row,
                                      })
                                    }
                                  >
                                    <VisibilityIcon />
                                  </IconButton>
                                  <IconButton
                                    aria-label="edit"
                                    onClick={() =>
                                      setOpenEditDialog({
                                        open: true,
                                        item: row,
                                      })
                                    }
                                  >
                                    <EditIcon />
                                  </IconButton>
                                </Box>
                              </TableCell>
                            );
                          }
                          return (
                            <TableCell key={column.id} align={"center"}>
                              {column.format && typeof value === "number"
                                ? column.format(value)
                                : value}
                            </TableCell>
                          );
                        })}
                      </TableRow>
                    );
                  })}
              </TableBody>
            </Table>
            <TablePagination
              rowsPerPageOptions={[10, 25, 100]}
              component="div"
              count={tota}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </CardBody>
        </Card>
      </GridItem>
      {/*<Fab color="primary" aria-label="add" className={classes.fab}*/}
      {/*     onClick={() => setOpenAddDialog(true)}>*/}
      {/*    <AddIcon/>*/}
      {/*</Fab>*/}

      {openAddDialog && (
        <DiscountAdd
          fetchData={refreshDiscounts}
          open={openAddDialog}
          close={closeAddDialog}
        />
      )}
      {openViewDialog ? (
        <DiscountView
          open={openViewDialog.open}
          close={closeViewDialog}
          item={openViewDialog.item}
        />
      ) : null}
      {openEditDialog ? (
        <DiscountEdit
          open={openEditDialog.open}
          close={closeEditDialog}
          item={openEditDialog.item}
          fetchData={refreshDiscounts}
        />
      ) : null}
      <ImportExcel
        db={"discounts"}
        open={openImportDialog}
        close={() => setOpenImportDialog(false)}
      />
      {openClearAllConfirm ? (
        <ConfirmationDialog
          open={!!openClearAllConfirm}
          onClose={() => setOpenClearAllConfirm(null)}
          onConfirm={handleClearAllDiscountsSelected}
          title="Clear all discounts"
          msg={`Clear all discounts for the ${selectedRows.length} selected account(s)?`}
        />
      ) : null}
      <Backdrop
        open={isExporting}
        style={{
          zIndex: 9999,
          color: '#fff',
          backgroundColor: 'rgba(0, 0, 0, 0.7)',
        }}
      >
        <Box
          display="flex"
          flexDirection="column"
          alignItems="center"
          justifyContent="center"
        >
          <CircularProgress size={60} thickness={4} style={{ color: '#512da8', marginBottom: 20 }} />
          <Box style={{ fontSize: '18px', fontWeight: 'bold', marginTop: 10 }}>
            Exporting Data...
          </Box>
          <Box style={{ fontSize: '14px', marginTop: 5, opacity: 0.8 }}>
            Please wait while we prepare your Excel file
          </Box>
        </Box>
      </Backdrop>
    </GridContainer>
  );
}
